import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  Save, Plus, Trash2, Edit, Eye, EyeOff, X,
  ChevronDown, ChevronUp, Image, Type, List, FileText,
  Layout, Users, MessageSquare, HelpCircle, Phone, Star,
  Settings, RefreshCw, Copy, ExternalLink, MapPin,
  Video, Briefcase, BarChart3, Megaphone, Search, Globe,
  Tag, ImageIcon, Code, PanelRightOpen, PanelRightClose
} from 'lucide-react';
import ImageUploader from '@/components/ImageUploader';
import RichTextEditor from '@/components/RichTextEditor';
import { adminFetch } from '../../utils/adminApi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SECTION_TYPES = {
  hero: { name: 'Hero Banner', icon: Layout },
  water_awareness: { name: 'Water Awareness', icon: BarChart3 },
  text: { name: 'Text Content', icon: Type },
  features: { name: 'Features Grid', icon: List },
  services: { name: 'Services', icon: Settings },
  products: { name: 'Products Showcase', icon: Layout },
  rent_section: { name: 'RO on Rent', icon: Megaphone },
  how_it_works: { name: 'How It Works', icon: List },
  testimonials: { name: 'Testimonials', icon: MessageSquare },
  team: { name: 'Team Members', icon: Users },
  faq: { name: 'FAQ', icon: HelpCircle },
  contact: { name: 'Contact Section', icon: Phone },
  cta: { name: 'Call to Action', icon: Megaphone },
  stats: { name: 'Statistics', icon: BarChart3 },
  gallery: { name: 'Image Gallery', icon: Image },
  why_choose_us: { name: 'Why Choose Us', icon: Star },
  policy: { name: 'Policy Content', icon: FileText },
  about_content: { name: 'About Content', icon: FileText },
  map: { name: 'Map Embed', icon: MapPin },
  video: { name: 'Video Embed', icon: Video },
  brands: { name: 'Brands / Partners', icon: Briefcase },
};

/* ========== MAIN CMS COMPONENT ========== */
function AdminCMS() {
  const [pages, setPages] = useState([]);
  const [sections, setSections] = useState([]);
  const [activePage, setActivePage] = useState('home');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [showAddSection, setShowAddSection] = useState(false);
  const [showSEO, setShowSEO] = useState(false);
  const [seoData, setSeoData] = useState({});

  useEffect(() => { fetchPages(); }, []);
  useEffect(() => { if (activePage) { fetchSections(activePage); fetchSEO(activePage); } }, [activePage]);

  const fetchPages = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/pages`);
      if (res.ok) setPages(await res.json());
    } catch { toast.error('Failed to load pages'); }
  };

  const fetchSections = async (pageId) => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/sections/${pageId}`);
      if (res.ok) setSections((await res.json()).sort((a, b) => (a.order || 0) - (b.order || 0)));
    } catch { toast.error('Failed to load sections'); }
    finally { setLoading(false); }
  };

  const fetchSEO = async (pageId) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/seo/${pageId}`);
      if (res.ok) setSeoData(await res.json());
    } catch { /* ignore */ }
  };

  const saveSection = async (sectionData) => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/sections/${sectionData.section_id}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(sectionData)
      });
      if (res.ok) { toast.success('Section saved!'); fetchSections(activePage); setEditingSection(null); }
      else toast.error('Failed to save');
    } catch { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const addSection = async (type) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/sections`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ page: activePage, section_type: type, title: `New ${SECTION_TYPES[type]?.name || type}`, subtitle: '', content: '', order: sections.length + 1, is_visible: true })
      });
      if (res.ok) { toast.success('Section added!'); fetchSections(activePage); setShowAddSection(false); }
    } catch { toast.error('Failed to add'); }
  };

  const deleteSection = async (id) => {
    if (!confirm('Delete this section permanently?')) return;
    try {
      const res = await fetch(`${BACKEND_URL}/api/cms/sections/${id}`, { method: 'DELETE' });
      if (res.ok) { toast.success('Deleted'); fetchSections(activePage); }
    } catch { toast.error('Delete failed'); }
  };

  const duplicateSection = async (id) => {
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/cms/sections/${id}/duplicate`, { method: 'POST' });
      if (res.ok) { toast.success('Duplicated!'); fetchSections(activePage); }
    } catch { toast.error('Duplicate failed'); }
  };

  const toggleVisibility = async (section) => {
    await saveSection({ ...section, is_visible: !section.is_visible });
  };

  const moveSection = async (index, direction) => {
    const arr = [...sections];
    const ni = direction === 'up' ? index - 1 : index + 1;
    if (ni < 0 || ni >= arr.length) return;
    [arr[index], arr[ni]] = [arr[ni], arr[index]];
    arr.forEach((s, i) => { s.order = i + 1; });
    setSections(arr);
    try {
      await adminFetch(`${BACKEND_URL}/api/admin/cms/sections/reorder`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(arr.map((s, i) => ({ section_id: s.section_id, order: i + 1 })))
      });
    } catch { toast.error('Reorder failed'); }
  };

  const sectionPreview = (s) => {
    const parts = [];
    if (s.image) parts.push(<img key="img" src={s.image} alt="" className="w-24 h-16 object-cover rounded-lg border" />);
    if (s.subtitle) parts.push(<p key="sub" className="text-xs text-blue-600 font-medium">{s.subtitle}</p>);
    const plain = (s.content || '').replace(/<[^>]*>/g, '').trim();
    if (plain) parts.push(<p key="cnt" className="text-gray-500 text-xs line-clamp-2">{plain.substring(0, 180)}</p>);
    const c = [];
    if (s.items?.length) c.push(`${s.items.length} items`);
    if (s.services?.length) c.push(`${s.services.length} services`);
    if (s.testimonials?.length) c.push(`${s.testimonials.length} testimonials`);
    if (s.reasons?.length) c.push(`${s.reasons.length} reasons`);
    if (s.images?.length) c.push(`${s.images.length} images`);
    if (s.stats?.length) c.push(`${s.stats.length} stats`);
    if (c.length) parts.push(<p key="c" className="text-[11px] text-gray-400 mt-1">{c.join(' | ')}</p>);
    return parts.length ? parts : <p className="text-xs text-gray-400 italic">No content — click Edit</p>;
  };

  return (
    <div className="min-h-screen" data-testid="admin-cms-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900" data-testid="cms-page-title">Page Editor</h1>
          <p className="text-gray-500 text-sm">Edit all website pages, content, images & SEO</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowSEO(!showSEO)} data-testid="toggle-seo-btn"
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${showSEO ? 'bg-green-600 text-white' : 'text-green-700 bg-green-50 hover:bg-green-100 border border-green-200'}`}>
            <Search size={16} /> SEO Tools
          </button>
          <a href="/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg text-sm" data-testid="preview-site-btn">
            <ExternalLink size={16} /> Preview Site
          </a>
        </div>
      </div>

      {/* Page Tabs */}
      <div className="bg-white rounded-xl shadow-sm mb-4 overflow-x-auto" data-testid="cms-page-tabs">
        <div className="flex border-b">
          {pages.map(page => (
            <button key={page.id} onClick={() => setActivePage(page.id)} data-testid={`cms-tab-${page.id}`}
              className={`px-5 py-3.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                activePage === page.id ? 'border-blue-600 text-blue-600 bg-blue-50/60' : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}>{page.name}</button>
          ))}
        </div>
      </div>

      {/* SEO Panel */}
      {showSEO && <SEOPanel pageId={activePage} seoData={seoData} onUpdate={setSeoData} />}

      {/* RO on Rent — Quick link to manage product model images */}
      {activePage === 'ro-on-rent' && (
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4 mb-4 flex items-center justify-between" data-testid="rent-models-banner">
          <div className="flex items-center gap-3">
            <div className="bg-blue-100 p-2 rounded-lg"><Image className="text-blue-600" size={20} /></div>
            <div>
              <p className="font-semibold text-gray-900 text-sm">Want to change RO product images & pricing?</p>
              <p className="text-gray-500 text-xs">Product model photos, prices, and features are managed separately</p>
            </div>
          </div>
          <a href="/admin/rent-models" className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium whitespace-nowrap" data-testid="go-to-rent-models-btn">
            <Image size={14} /> Manage Product Images
          </a>
        </div>
      )}

      {/* Sections */}
      <div className="space-y-3">
        {loading ? (
          <div className="bg-white rounded-xl p-12 flex items-center justify-center"><RefreshCw className="w-6 h-6 text-blue-500 animate-spin" /></div>
        ) : sections.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center" data-testid="cms-empty-state">
            <Layout className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <h3 className="text-base font-medium text-gray-900 mb-1">No sections yet</h3>
            <p className="text-gray-500 text-sm mb-4">Add your first section to this page</p>
            <button onClick={() => setShowAddSection(true)} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm" data-testid="add-first-section-btn">
              <Plus size={16} className="inline mr-1" /> Add Section
            </button>
          </div>
        ) : (
          sections.map((section, index) => (
            <div key={section.section_id} data-testid={`cms-section-${section.section_id}`}
              className={`bg-white rounded-xl shadow-sm overflow-hidden transition-opacity ${!section.is_visible ? 'opacity-50' : ''}`}>
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50/80 border-b">
                <div className="flex items-center gap-3">
                  <div className="flex flex-col">
                    <button onClick={() => moveSection(index, 'up')} disabled={index === 0} className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-20" data-testid={`move-up-${section.section_id}`}><ChevronUp size={14} /></button>
                    <button onClick={() => moveSection(index, 'down')} disabled={index === sections.length - 1} className="p-0.5 hover:bg-gray-200 rounded disabled:opacity-20" data-testid={`move-down-${section.section_id}`}><ChevronDown size={14} /></button>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[11px] font-semibold rounded">{SECTION_TYPES[section.section_type]?.name || section.section_type}</span>
                      {!section.is_visible && <span className="px-2 py-0.5 bg-gray-200 text-gray-500 text-[11px] rounded">Hidden</span>}
                    </div>
                    <h3 className="font-semibold text-gray-900 text-sm mt-0.5">{section.title || 'Untitled'}</h3>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => toggleVisibility(section)} className={`p-1.5 rounded-lg ${section.is_visible ? 'text-green-600 hover:bg-green-50' : 'text-gray-400 hover:bg-gray-100'}`} data-testid={`toggle-vis-${section.section_id}`}>
                    {section.is_visible ? <Eye size={16} /> : <EyeOff size={16} />}
                  </button>
                  <button onClick={() => duplicateSection(section.section_id)} title="Duplicate" className="p-1.5 text-gray-500 hover:bg-gray-100 rounded-lg" data-testid={`duplicate-${section.section_id}`}><Copy size={16} /></button>
                  <button onClick={() => setEditingSection({ ...section })} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-xs font-medium flex items-center gap-1.5" data-testid={`edit-${section.section_id}`}><Edit size={14} /> Edit</button>
                  <button onClick={() => deleteSection(section.section_id)} className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg" data-testid={`delete-${section.section_id}`}><Trash2 size={16} /></button>
                </div>
              </div>
              <div className="px-4 py-3 space-y-1">{sectionPreview(section)}</div>
            </div>
          ))
        )}
        <button onClick={() => setShowAddSection(true)} data-testid="add-section-btn"
          className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-500 hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50/50 transition-colors flex items-center justify-center gap-2 text-sm">
          <Plus size={18} /> Add New Section
        </button>
      </div>

      {showAddSection && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-testid="add-section-modal">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold">Choose Section Type</h3>
              <button onClick={() => setShowAddSection(false)} className="p-1.5 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
            </div>
            <div className="p-4 grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(SECTION_TYPES).map(([type, config]) => (
                <button key={type} onClick={() => addSection(type)} data-testid={`add-type-${type}`}
                  className="p-3 border-2 border-gray-200 rounded-xl hover:border-blue-500 hover:bg-blue-50 transition-colors text-left">
                  <config.icon className="w-6 h-6 text-blue-600 mb-1.5" />
                  <h4 className="font-medium text-gray-900 text-sm">{config.name}</h4>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {editingSection && (
        <SectionEditor section={editingSection} onSave={saveSection} onClose={() => setEditingSection(null)} saving={saving} />
      )}
    </div>
  );
}

/* ========== SEO PANEL ========== */
function SEOPanel({ pageId, seoData, onUpdate }) {
  const [data, setData] = useState(seoData);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setData(seoData); }, [seoData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await adminFetch(`${BACKEND_URL}/api/admin/cms/seo/${pageId}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data)
      });
      if (res.ok) {
        const updated = await res.json();
        onUpdate(updated);
        toast.success('SEO settings saved!');
      } else toast.error('Failed to save SEO');
    } catch { toast.error('Save error'); }
    finally { setSaving(false); }
  };

  const titleLen = (data.meta_title || '').length;
  const descLen = (data.meta_description || '').length;

  return (
    <div className="bg-white rounded-xl shadow-sm mb-4 p-5 border-l-4 border-green-500" data-testid="seo-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Search className="text-green-600" size={20} />
          <h3 className="font-semibold text-gray-900">SEO Settings — {pageId.charAt(0).toUpperCase() + pageId.slice(1)} Page</h3>
        </div>
        <button onClick={handleSave} disabled={saving} data-testid="save-seo-btn"
          className="px-4 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5">
          {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save SEO
        </button>
      </div>

      {/* Google Preview */}
      <div className="bg-gray-50 rounded-lg p-4 mb-4 border" data-testid="seo-google-preview">
        <p className="text-xs text-gray-400 mb-2 flex items-center gap-1"><Globe size={12} /> Google Search Preview</p>
        <div className="text-blue-700 text-lg font-medium hover:underline cursor-pointer truncate">
          {data.meta_title || `${pageId.charAt(0).toUpperCase() + pageId.slice(1)} - RO Dekho`}
        </div>
        <div className="text-green-700 text-sm truncate">www.rodekho.in/{pageId === 'home' ? '' : pageId}</div>
        <div className="text-gray-600 text-sm line-clamp-2 mt-0.5">
          {data.meta_description || 'Add a meta description to improve how this page appears in search results.'}
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Tag size={14} className="inline mr-1" /> Meta Title
            <span className={`ml-2 text-xs ${titleLen > 60 ? 'text-red-500' : titleLen > 50 ? 'text-yellow-500' : 'text-gray-400'}`}>{titleLen}/60</span>
          </label>
          <input type="text" value={data.meta_title || ''} onChange={e => setData({ ...data, meta_title: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="Page Title - RO Dekho | Your Keyword" data-testid="seo-meta-title" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            <Tag size={14} className="inline mr-1" /> Meta Keywords
          </label>
          <input type="text" value={data.meta_keywords || ''} onChange={e => setData({ ...data, meta_keywords: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="RO service Jaipur, water purifier, RO repair" data-testid="seo-meta-keywords" />
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          <FileText size={14} className="inline mr-1" /> Meta Description
          <span className={`ml-2 text-xs ${descLen > 160 ? 'text-red-500' : descLen > 140 ? 'text-yellow-500' : 'text-gray-400'}`}>{descLen}/160</span>
        </label>
        <textarea value={data.meta_description || ''} onChange={e => setData({ ...data, meta_description: e.target.value })}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-y" rows={3}
          placeholder="Describe this page in 150-160 characters for search engines..." data-testid="seo-meta-description" />
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1"><ImageIcon size={14} className="inline mr-1" /> OG Image URL</label>
          <input type="text" value={data.og_image || ''} onChange={e => setData({ ...data, og_image: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="https://rodekho.in/og-image.jpg" data-testid="seo-og-image" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1"><Globe size={14} className="inline mr-1" /> Canonical URL</label>
          <input type="text" value={data.canonical_url || ''} onChange={e => setData({ ...data, canonical_url: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500"
            placeholder="https://www.rodekho.in/about" data-testid="seo-canonical" />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1"><Code size={14} className="inline mr-1" /> Schema Type</label>
          <select value={data.schema_type || 'WebPage'} onChange={e => setData({ ...data, schema_type: e.target.value })}
            className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:border-green-500" data-testid="seo-schema-type">
            <option value="WebPage">WebPage</option>
            <option value="Organization">Organization</option>
            <option value="LocalBusiness">LocalBusiness</option>
            <option value="Product">Product</option>
            <option value="FAQPage">FAQPage</option>
            <option value="ContactPage">ContactPage</option>
            <option value="AboutPage">AboutPage</option>
          </select>
        </div>
        <div className="flex items-end">
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={data.no_index || false} onChange={e => setData({ ...data, no_index: e.target.checked })} className="w-4 h-4 text-green-600 rounded" data-testid="seo-noindex" />
            <span className="text-sm text-gray-700">No Index (hide from search engines)</span>
          </label>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1"><Code size={14} className="inline mr-1" /> Custom Head Tags</label>
        <textarea value={data.custom_head_tags || ''} onChange={e => setData({ ...data, custom_head_tags: e.target.value })}
          className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500 resize-y" rows={2}
          placeholder='<meta property="og:locale" content="en_IN" />' data-testid="seo-custom-tags" />
      </div>
    </div>
  );
}

/* ========== REUSABLE COMPONENTS ========== */
function Field({ label, children, hint }) {
  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1.5">{label}
        {hint && <span className="text-xs text-gray-400 ml-1.5 font-normal">{hint}</span>}
      </label>
      {children}
    </div>
  );
}

const inputCls = "w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm";
const textareaCls = `${inputCls} resize-y`;

function ListManager({ label, items, onAdd, onRemove, onUpdate, renderItem, addLabel = '+ Add' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <button type="button" onClick={onAdd} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid={`add-${label.toLowerCase().replace(/\s/g,'-')}`}>{addLabel}</button>
      </div>
      <div className="space-y-3">
        {(items || []).map((item, i) => (
          <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 relative">
            <button type="button" onClick={() => onRemove(i)} className="absolute top-2 right-2 p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded" data-testid={`remove-item-${i}`}><Trash2 size={14} /></button>
            {renderItem(item, i, (val) => onUpdate(i, val))}
          </div>
        ))}
        {(!items || items.length === 0) && <p className="text-xs text-gray-400 italic py-2">No items yet. Click "{addLabel}" to add.</p>}
      </div>
    </div>
  );
}

/* ========== LIVE PREVIEW ========== */
function LivePreview({ fd }) {
  const type = fd.section_type;

  if (type === 'hero') return (
    <div className="bg-gradient-to-br from-blue-900 to-blue-800 rounded-lg p-6 text-white min-h-[200px]">
      {fd.badge_text && <span className="inline-block bg-lime-400/20 text-lime-300 text-[10px] px-2 py-0.5 rounded-full mb-2 border border-lime-400/30">{fd.badge_text}</span>}
      <h2 className="text-lg font-bold mb-1">{fd.title || 'Hero Title'}</h2>
      {fd.subtitle && <p className="text-blue-200 text-xs mb-2">{fd.subtitle}</p>}
      {fd.content && <p className="text-blue-100/80 text-[10px] mb-3 line-clamp-3">{fd.content}</p>}
      {fd.image && <img src={fd.image} alt="" className="w-full h-20 object-cover rounded mb-2" />}
      <div className="flex gap-2 mt-2">
        {fd.cta_primary && <span className="bg-lime-400 text-gray-900 text-[10px] px-2 py-1 rounded font-semibold">{fd.cta_primary}</span>}
        {fd.cta_secondary && <span className="border border-white/30 text-[10px] px-2 py-1 rounded">{fd.cta_secondary}</span>}
      </div>
      {fd.stats?.length > 0 && (
        <div className="flex gap-3 mt-3 pt-3 border-t border-white/20">
          {fd.stats.map((s, i) => <div key={i} className="text-center"><div className="text-lime-400 text-sm font-bold">{s.value}</div><div className="text-[9px] text-blue-200">{s.label}</div></div>)}
        </div>
      )}
    </div>
  );

  if (type === 'services') return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-1">{fd.title || 'Services'}</h3>
      {fd.subtitle && <p className="text-[10px] text-blue-600 mb-2">{fd.subtitle}</p>}
      <div className="grid grid-cols-2 gap-2">
        {(fd.services || []).slice(0, 4).map((s, i) => (
          <div key={i} className="bg-gray-50 rounded p-2">
            <div className="text-[11px] font-semibold text-gray-900">{s.title || 'Service'}</div>
            {s.price && <div className="text-[10px] text-blue-600">{s.price}</div>}
          </div>
        ))}
      </div>
      {(fd.services || []).length > 4 && <p className="text-[9px] text-gray-400 mt-1">+ {fd.services.length - 4} more</p>}
    </div>
  );

  if (type === 'testimonials') return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-2">{fd.title || 'Testimonials'}</h3>
      <div className="space-y-2">
        {(fd.testimonials || []).slice(0, 2).map((t, i) => (
          <div key={i} className="bg-gray-50 rounded p-2">
            <div className="flex items-center gap-1 mb-1">
              <span className="text-[11px] font-semibold">{t.name || 'Customer'}</span>
              <span className="text-yellow-500 text-[10px]">{'★'.repeat(t.rating || 5)}</span>
            </div>
            <p className="text-[10px] text-gray-600 line-clamp-2">{t.content}</p>
          </div>
        ))}
      </div>
    </div>
  );

  if (type === 'faq') return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-2">{fd.title || 'FAQ'}</h3>
      <div className="space-y-1.5">
        {(fd.items || []).slice(0, 3).map((q, i) => (
          <div key={i} className="bg-gray-50 rounded p-2">
            <div className="text-[11px] font-semibold text-gray-900">{q.question || 'Question?'}</div>
            <p className="text-[10px] text-gray-500 line-clamp-1">{q.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );

  if (type === 'contact') return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-2">{fd.title || 'Contact'}</h3>
      <div className="space-y-1.5 text-[11px]">
        {fd.phone && <div className="flex items-center gap-1"><Phone size={10} />{fd.phone}</div>}
        {fd.email && <div className="flex items-center gap-1"><span>@</span>{fd.email}</div>}
        {fd.whatsapp && <div className="flex items-center gap-1"><MessageSquare size={10} />{fd.whatsapp}</div>}
        {fd.address && <div className="flex items-center gap-1"><MapPin size={10} /><span className="line-clamp-1">{fd.address}</span></div>}
      </div>
    </div>
  );

  if (type === 'why_choose_us') return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-2">{fd.title || 'Why Choose Us'}</h3>
      <div className="grid grid-cols-2 gap-1.5">
        {(fd.reasons || []).slice(0, 4).map((r, i) => (
          <div key={i} className="bg-blue-50 rounded p-2"><div className="text-[11px] font-semibold text-blue-800">{r.title}</div></div>
        ))}
      </div>
    </div>
  );

  if (type === 'stats') return (
    <div className="bg-blue-900 rounded-lg p-4 text-white">
      <h3 className="text-sm font-bold mb-2">{fd.title || 'Stats'}</h3>
      <div className="flex gap-3">
        {(fd.items || []).map((s, i) => <div key={i} className="text-center"><div className="text-lime-400 text-sm font-bold">{s.value}</div><div className="text-[9px] text-blue-200">{s.label}</div></div>)}
      </div>
    </div>
  );

  if (type === 'policy') return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-1">{fd.title || 'Policy'}</h3>
      <p className="text-[10px] text-gray-500 line-clamp-6 whitespace-pre-line">{fd.content}</p>
    </div>
  );

  // Default preview
  return (
    <div className="bg-white rounded-lg p-4">
      <h3 className="text-sm font-bold text-gray-900 mb-1">{fd.title || 'Section'}</h3>
      {fd.subtitle && <p className="text-[10px] text-blue-600 mb-1">{fd.subtitle}</p>}
      {fd.image && <img src={fd.image} alt="" className="w-full h-20 object-cover rounded mb-2" />}
      {fd.content && <p className="text-[10px] text-gray-500 line-clamp-4">{fd.content}</p>}
    </div>
  );
}

/* ========== SECTION EDITOR WITH LIVE PREVIEW ========== */
function SectionEditor({ section, onSave, onClose, saving }) {
  const [fd, setFd] = useState({ ...section });
  const [showPreview, setShowPreview] = useState(true);

  const set = useCallback((field, value) => setFd(prev => ({ ...prev, [field]: value })), []);
  const updateListItem = useCallback((field, index, value) => {
    setFd(prev => { const items = [...(prev[field] || [])]; items[index] = { ...items[index], ...value }; return { ...prev, [field]: items }; });
  }, []);
  const addListItem = useCallback((field, template) => {
    setFd(prev => ({ ...prev, [field]: [...(prev[field] || []), template] }));
  }, []);
  const removeListItem = useCallback((field, index) => {
    setFd(prev => { const items = [...(prev[field] || [])]; items.splice(index, 1); return { ...prev, [field]: items }; });
  }, []);

  const handleSubmit = (e) => { e.preventDefault(); onSave(fd); };
  const type = section.section_type;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto" data-testid="section-editor-modal">
      <div className={`bg-white rounded-xl my-6 shadow-2xl flex flex-col ${showPreview ? 'max-w-6xl' : 'max-w-4xl'} w-full`}>
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-5 py-3 flex items-center justify-between z-10 rounded-t-xl">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-[11px] font-semibold rounded">{SECTION_TYPES[type]?.name || type}</span>
            <span className="text-sm font-semibold text-gray-800">Edit Section</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowPreview(!showPreview)} className={`p-1.5 rounded-lg text-sm flex items-center gap-1 ${showPreview ? 'bg-indigo-100 text-indigo-700' : 'text-gray-500 hover:bg-gray-100'}`} data-testid="toggle-preview-btn">
              {showPreview ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
              <span className="text-xs hidden sm:inline">{showPreview ? 'Hide' : 'Show'} Preview</span>
            </button>
            <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm" data-testid="editor-cancel-btn">Cancel</button>
            <button onClick={handleSubmit} disabled={saving} data-testid="editor-save-btn"
              className="px-4 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium flex items-center gap-1.5">
              {saving ? <RefreshCw size={14} className="animate-spin" /> : <Save size={14} />} Save
            </button>
          </div>
        </div>

        <div className="flex flex-1 min-h-0">
          {/* Editor Form */}
          <form onSubmit={handleSubmit} className={`p-5 space-y-5 overflow-y-auto ${showPreview ? 'w-3/5' : 'w-full'}`} style={{ maxHeight: 'calc(90vh - 56px)' }}>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Title"><input type="text" value={fd.title || ''} onChange={e => set('title', e.target.value)} className={inputCls} placeholder="Section title" data-testid="editor-title" /></Field>
              <Field label="Subtitle"><input type="text" value={fd.subtitle || ''} onChange={e => set('subtitle', e.target.value)} className={inputCls} placeholder="Section subtitle" data-testid="editor-subtitle" /></Field>
            </div>

            {type !== 'gallery' && type !== 'map' && (
              <Field label="Content">
                {['policy', 'about_content', 'text', 'hero', 'contact'].includes(type) ? (
                  <RichTextEditor value={fd.content || ''} onChange={val => set('content', val)} placeholder="Main content..." minHeight={type === 'policy' ? '280px' : '160px'} />
                ) : (
                  <textarea value={fd.content || ''} onChange={e => set('content', e.target.value)} rows={4} className={textareaCls} placeholder="Main content..." data-testid="editor-content" />
                )}
              </Field>
            )}

            {['hero', 'text', 'cta', 'about_content', 'testimonials', 'services', 'rent_section', 'features', 'stats', 'brands'].includes(type) && (
              <ImageUploader value={fd.image || ''} onChange={url => set('image', url)} folder="cms" type="hero" label="Section Image" />
            )}

            {type === 'hero' && <HeroFields fd={fd} set={set} addListItem={addListItem} updateListItem={updateListItem} removeListItem={removeListItem} />}
            {type === 'contact' && <ContactFields fd={fd} set={set} />}

            {type === 'services' && (
              <ListManager label="Services" items={fd.services} addLabel="+ Add Service"
                onAdd={() => addListItem('services', { id: `srv_${Date.now()}`, title: '', description: '', price: '', icon: 'Wrench', image: '' })}
                onRemove={i => removeListItem('services', i)} onUpdate={(i, v) => updateListItem('services', i, v)}
                renderItem={(srv, i, update) => (
                  <div className="space-y-2 pr-6">
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={srv.title || ''} onChange={e => update({ title: e.target.value })} className={inputCls} placeholder="Service Title" data-testid={`service-title-${i}`} />
                      <input type="text" value={srv.price || ''} onChange={e => update({ price: e.target.value })} className={inputCls} placeholder="Price" data-testid={`service-price-${i}`} />
                    </div>
                    <textarea value={srv.description || ''} onChange={e => update({ description: e.target.value })} className={textareaCls} placeholder="Description" rows={2} data-testid={`service-desc-${i}`} />
                    <ImageUploader value={srv.image || ''} onChange={url => update({ image: url })} folder="services" type="service" label={`Service ${i+1} Image`} />
                  </div>
                )} />
            )}

            {type === 'testimonials' && (
              <ListManager label="Testimonials" items={fd.testimonials} addLabel="+ Add Testimonial"
                onAdd={() => addListItem('testimonials', { id: `t_${Date.now()}`, name: '', location: '', rating: 5, content: '', image: '' })}
                onRemove={i => removeListItem('testimonials', i)} onUpdate={(i, v) => updateListItem('testimonials', i, v)}
                renderItem={(t, i, update) => (
                  <div className="space-y-2 pr-6">
                    <div className="grid grid-cols-3 gap-2">
                      <input type="text" value={t.name || ''} onChange={e => update({ name: e.target.value })} className={inputCls} placeholder="Name" data-testid={`testimonial-name-${i}`} />
                      <input type="text" value={t.location || ''} onChange={e => update({ location: e.target.value })} className={inputCls} placeholder="Location" data-testid={`testimonial-loc-${i}`} />
                      <select value={t.rating || 5} onChange={e => update({ rating: parseInt(e.target.value) })} className={inputCls} data-testid={`testimonial-rating-${i}`}>
                        {[5,4,3,2,1].map(r => <option key={r} value={r}>{r} Stars</option>)}
                      </select>
                    </div>
                    <textarea value={t.content || ''} onChange={e => update({ content: e.target.value })} className={textareaCls} placeholder="Review" rows={2} data-testid={`testimonial-content-${i}`} />
                    <ImageUploader value={t.image || ''} onChange={url => update({ image: url })} folder="cms" type="thumbnail" label="Photo (optional)" />
                  </div>
                )} />
            )}

            {type === 'faq' && (
              <>
                <ImageUploader value={fd.image || ''} onChange={url => set('image', url)} folder="cms" type="hero" label="FAQ Section Image (optional)" />
                <ListManager label="FAQ Items" items={fd.items} addLabel="+ Add FAQ"
                  onAdd={() => addListItem('items', { question: '', answer: '' })}
                  onRemove={i => removeListItem('items', i)} onUpdate={(i, v) => updateListItem('items', i, v)}
                  renderItem={(item, i, update) => (
                    <div className="space-y-2 pr-6">
                      <input type="text" value={item.question || ''} onChange={e => update({ question: e.target.value })} className={inputCls} placeholder="Question" data-testid={`faq-q-${i}`} />
                      <textarea value={item.answer || ''} onChange={e => update({ answer: e.target.value })} className={textareaCls} placeholder="Answer" rows={3} data-testid={`faq-a-${i}`} />
                    </div>
                  )} />
              </>
            )}

            {type === 'why_choose_us' && (
              <>
                <ImageUploader value={fd.image || ''} onChange={url => set('image', url)} folder="cms" type="hero" label="Section Background Image (optional)" />
                <ListManager label="Reasons" items={fd.reasons} addLabel="+ Add Reason"
                  onAdd={() => addListItem('reasons', { icon: 'Star', title: '', description: '', image: '' })}
                  onRemove={i => removeListItem('reasons', i)} onUpdate={(i, v) => updateListItem('reasons', i, v)}
                  renderItem={(r, i, update) => (
                    <div className="space-y-2 pr-6">
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" value={r.title || ''} onChange={e => update({ title: e.target.value })} className={inputCls} placeholder="Title" data-testid={`reason-title-${i}`} />
                        <input type="text" value={r.icon || ''} onChange={e => update({ icon: e.target.value })} className={inputCls} placeholder="Icon (Award, Shield)" data-testid={`reason-icon-${i}`} />
                      </div>
                      <textarea value={r.description || ''} onChange={e => update({ description: e.target.value })} className={textareaCls} placeholder="Description" rows={2} data-testid={`reason-desc-${i}`} />
                      <ImageUploader value={r.image || ''} onChange={url => update({ image: url })} folder="cms" type="thumbnail" label={`Reason ${i+1} Image (optional)`} />
                    </div>
                  )} />
              </>
            )}

            {type === 'water_awareness' && (
              <>
                <ImageUploader value={fd.image || ''} onChange={url => set('image', url)} folder="cms" type="hero" label="Water Awareness Section Image (optional)" />
                <ListManager label="Water Stats" items={fd.items} addLabel="+ Add Stat"
                  onAdd={() => addListItem('items', { value: '', label: '', detail: '', image: '' })}
                  onRemove={i => removeListItem('items', i)} onUpdate={(i, v) => updateListItem('items', i, v)}
                  renderItem={(item, i, update) => (
                    <div className="space-y-2 pr-6">
                      <div className="grid grid-cols-2 gap-2">
                        <input type="text" value={item.value || ''} onChange={e => update({ value: e.target.value })} className={inputCls} placeholder="Stat value (e.g. 70%)" data-testid={`awareness-val-${i}`} />
                        <input type="text" value={item.label || ''} onChange={e => update({ label: e.target.value })} className={inputCls} placeholder="Label" data-testid={`awareness-label-${i}`} />
                      </div>
                      <textarea value={item.detail || ''} onChange={e => update({ detail: e.target.value })} className={textareaCls} placeholder="Detail description" rows={2} data-testid={`awareness-detail-${i}`} />
                      <ImageUploader value={item.image || ''} onChange={url => update({ image: url })} folder="cms" type="thumbnail" label={`Stat ${i+1} Image (optional)`} />
                    </div>
                  )} />
              </>
            )}

            {type === 'how_it_works' && (
              <>
                <ImageUploader value={fd.image || ''} onChange={url => set('image', url)} folder="cms" type="hero" label="Section Image (optional)" />
                <ListManager label="Steps" items={fd.items} addLabel="+ Add Step"
                  onAdd={() => addListItem('items', { title: '', description: '', image: '' })}
                  onRemove={i => removeListItem('items', i)} onUpdate={(i, v) => updateListItem('items', i, v)}
                  renderItem={(item, i, update) => (
                    <div className="space-y-2 pr-6">
                      <input type="text" value={item.title || ''} onChange={e => update({ title: e.target.value })} className={inputCls} placeholder={`Step ${i+1} Title`} data-testid={`step-title-${i}`} />
                      <textarea value={item.description || ''} onChange={e => update({ description: e.target.value })} className={textareaCls} placeholder="Step description" rows={2} data-testid={`step-desc-${i}`} />
                      <ImageUploader value={item.image || ''} onChange={url => update({ image: url })} folder="cms" type="thumbnail" label={`Step ${i+1} Image (optional)`} />
                    </div>
                  )} />
              </>
            )}

            {type === 'rent_section' && (
              <>
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Highlight Text"><input type="text" value={fd.highlight_text || ''} onChange={e => set('highlight_text', e.target.value)} className={inputCls} placeholder="On Rent from ₹350/mo" data-testid="rent-highlight" /></Field>
                  <Field label="CTA Button Text"><input type="text" value={fd.cta_text || ''} onChange={e => set('cta_text', e.target.value)} className={inputCls} placeholder="View Rental Plans" data-testid="rent-cta-text" /></Field>
                </div>
                <ListManager label="Benefits (₹0 items)" items={fd.items} addLabel="+ Add Benefit"
                  onAdd={() => addListItem('items', 'New Benefit')}
                  onRemove={i => removeListItem('items', i)} onUpdate={(i, v) => updateListItem('items', i, v)}
                  renderItem={(item, i, update) => (
                    <div className="pr-6">
                      <input type="text" value={typeof item === 'string' ? item : (item.text || '')}
                        onChange={e => update(e.target.value)} className={inputCls}
                        placeholder="e.g. Machine Cost" data-testid={`rent-benefit-${i}`} />
                    </div>
                  )} />
              </>
            )}

            {(type === 'features' || type === 'stats') && (
              <ListManager label={type === 'stats' ? 'Statistics' : 'Feature Items'} items={fd.items}
                addLabel={type === 'stats' ? '+ Add Stat' : '+ Add Feature'}
                onAdd={() => addListItem('items', type === 'stats' ? { value: '', label: '' } : { icon: 'Star', title: '', description: '' })}
                onRemove={i => removeListItem('items', i)} onUpdate={(i, v) => updateListItem('items', i, v)}
                renderItem={(item, i, update) => (
                  <div className="flex gap-2 items-start pr-6">
                    {type === 'stats' ? (
                      <><input type="text" value={item.value || ''} onChange={e => update({ value: e.target.value })} className={inputCls} placeholder="Value" data-testid={`stat-val-${i}`} />
                      <input type="text" value={item.label || ''} onChange={e => update({ label: e.target.value })} className={inputCls} placeholder="Label" data-testid={`stat-label-${i}`} /></>
                    ) : (
                      <div className="flex-1 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                          <input type="text" value={item.title || ''} onChange={e => update({ title: e.target.value })} className={inputCls} placeholder="Title" data-testid={`feature-title-${i}`} />
                          <input type="text" value={item.icon || ''} onChange={e => update({ icon: e.target.value })} className={inputCls} placeholder="Icon" data-testid={`feature-icon-${i}`} />
                        </div>
                        <textarea value={item.description || ''} onChange={e => update({ description: e.target.value })} className={textareaCls} placeholder="Description" rows={2} data-testid={`feature-desc-${i}`} />
                      </div>
                    )}
                  </div>
                )} />
            )}

            {(type === 'cta' || type === 'products') && (
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Button Text"><input type="text" value={fd.cta_text || ''} onChange={e => set('cta_text', e.target.value)} className={inputCls} placeholder="View All Products" data-testid="editor-cta-text" /></Field>
                <Field label="Button Link"><input type="text" value={fd.cta_link || ''} onChange={e => set('cta_link', e.target.value)} className={inputCls} placeholder="/products" data-testid="editor-cta-link" /></Field>
              </div>
            )}

            {type === 'about_content' && (
              <>
                <Field label="Mission"><RichTextEditor value={fd.mission || ''} onChange={val => set('mission', val)} placeholder="Our mission..." minHeight="120px" /></Field>
                <Field label="Vision"><RichTextEditor value={fd.vision || ''} onChange={val => set('vision', val)} placeholder="Our vision..." minHeight="120px" /></Field>
              </>
            )}

            {type === 'gallery' && <GalleryEditor images={fd.images || []} onChange={imgs => set('images', imgs)} />}

            {type === 'map' && (
              <Field label="Google Maps Embed URL" hint="Paste embed src from Google Maps">
                <input type="text" value={fd.map_embed || ''} onChange={e => set('map_embed', e.target.value)} className={inputCls} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="editor-map-embed" />
                {fd.map_embed && <div className="mt-2 rounded-lg overflow-hidden border"><iframe src={fd.map_embed} width="100%" height="200" style={{border:0}} loading="lazy" title="Map" /></div>}
              </Field>
            )}

            {type === 'video' && (
              <><Field label="Video URL" hint="YouTube or Vimeo embed URL"><input type="text" value={fd.video_url || ''} onChange={e => set('video_url', e.target.value)} className={inputCls} placeholder="https://www.youtube.com/embed/..." data-testid="editor-video-url" /></Field>
              {fd.video_url && <div className="rounded-lg overflow-hidden border aspect-video"><iframe src={fd.video_url} width="100%" height="100%" style={{border:0}} loading="lazy" allowFullScreen title="Video" /></div>}</>
            )}

            {type === 'brands' && (
              <ListManager label="Brand Logos" items={fd.brands || fd.items} addLabel="+ Add Brand"
                onAdd={() => addListItem(fd.brands ? 'brands' : 'items', { name: '', image: '', link: '' })}
                onRemove={i => removeListItem(fd.brands ? 'brands' : 'items', i)} onUpdate={(i, v) => updateListItem(fd.brands ? 'brands' : 'items', i, v)}
                renderItem={(brand, i, update) => (
                  <div className="space-y-2 pr-6">
                    <div className="grid grid-cols-2 gap-2">
                      <input type="text" value={brand.name || ''} onChange={e => update({ name: e.target.value })} className={inputCls} placeholder="Brand name" data-testid={`brand-name-${i}`} />
                      <input type="text" value={brand.link || ''} onChange={e => update({ link: e.target.value })} className={inputCls} placeholder="Website URL" data-testid={`brand-link-${i}`} />
                    </div>
                    <ImageUploader value={brand.image || ''} onChange={url => update({ image: url })} folder="cms" type="logo" label="Brand Logo" />
                  </div>
                )} />
            )}

            <div className="flex items-center gap-3 pt-4 border-t">
              <input type="checkbox" id="vis_toggle" checked={fd.is_visible !== false} onChange={e => set('is_visible', e.target.checked)} className="w-4 h-4 text-blue-600 rounded" data-testid="editor-visibility" />
              <label htmlFor="vis_toggle" className="text-sm text-gray-600">Show this section on the website</label>
            </div>
          </form>

          {/* Live Preview Panel */}
          {showPreview && (
            <div className="w-2/5 border-l bg-gray-100 p-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 56px)' }} data-testid="live-preview-panel">
              <div className="flex items-center gap-2 mb-3">
                <Eye size={14} className="text-indigo-600" />
                <span className="text-xs font-semibold text-indigo-700 uppercase tracking-wide">Live Preview</span>
              </div>
              <div className="bg-gray-200 rounded-lg p-2">
                <div className="bg-white rounded shadow-sm overflow-hidden">
                  <LivePreview fd={fd} />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 mt-2 text-center">This is an approximate preview. The actual page may look different.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ========== HERO FIELDS ========== */
function HeroFields({ fd, set, addListItem, updateListItem, removeListItem }) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Badge Text"><input type="text" value={fd.badge_text || ''} onChange={e => set('badge_text', e.target.value)} className={inputCls} placeholder="Trusted Provider" data-testid="editor-badge" /></Field>
        <Field label="Phone Number"><input type="tel" value={fd.phone || ''} onChange={e => set('phone', e.target.value)} className={inputCls} placeholder="+91 9887068888" data-testid="editor-phone" /></Field>
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Primary Button"><input type="text" value={fd.cta_primary || ''} onChange={e => set('cta_primary', e.target.value)} className={inputCls} placeholder="Book Service Now" data-testid="editor-cta-primary" /></Field>
        <Field label="Secondary Button"><input type="text" value={fd.cta_secondary || ''} onChange={e => set('cta_secondary', e.target.value)} className={inputCls} placeholder="Call Now" data-testid="editor-cta-secondary" /></Field>
      </div>
      <ListManager label="Statistics" items={fd.stats} addLabel="+ Add Stat"
        onAdd={() => addListItem('stats', { value: '', label: '' })} onRemove={i => removeListItem('stats', i)} onUpdate={(i, v) => updateListItem('stats', i, v)}
        renderItem={(stat, i, update) => (
          <div className="flex gap-2 pr-6">
            <input type="text" value={stat.value || ''} onChange={e => update({ value: e.target.value })} className={inputCls} placeholder="5000+" data-testid={`hero-stat-val-${i}`} />
            <input type="text" value={stat.label || ''} onChange={e => update({ label: e.target.value })} className={inputCls} placeholder="Label" data-testid={`hero-stat-label-${i}`} />
          </div>
        )} />
      <ListManager label="Trust Badges" items={fd.trust_badges} addLabel="+ Add Badge"
        onAdd={() => addListItem('trust_badges', { icon: 'CheckCircle', text: '' })} onRemove={i => removeListItem('trust_badges', i)} onUpdate={(i, v) => updateListItem('trust_badges', i, v)}
        renderItem={(badge, i, update) => (
          <div className="flex gap-2 pr-6">
            <input type="text" value={badge.icon || ''} onChange={e => update({ icon: e.target.value })} className={`${inputCls} w-1/3`} placeholder="Icon" data-testid={`hero-badge-icon-${i}`} />
            <input type="text" value={badge.text || ''} onChange={e => update({ text: e.target.value })} className={inputCls} placeholder="Badge text" data-testid={`hero-badge-text-${i}`} />
          </div>
        )} />
    </>
  );
}

/* ========== CONTACT FIELDS ========== */
function ContactFields({ fd, set }) {
  return (
    <>
      <div className="grid md:grid-cols-2 gap-4">
        <Field label="Phone"><input type="tel" value={fd.phone || ''} onChange={e => set('phone', e.target.value)} className={inputCls} data-testid="contact-phone" /></Field>
        <Field label="Email"><input type="email" value={fd.email || ''} onChange={e => set('email', e.target.value)} className={inputCls} data-testid="contact-email" /></Field>
        <Field label="WhatsApp"><input type="tel" value={fd.whatsapp || ''} onChange={e => set('whatsapp', e.target.value)} className={inputCls} data-testid="contact-whatsapp" /></Field>
        <Field label="Working Hours"><input type="text" value={fd.working_hours || ''} onChange={e => set('working_hours', e.target.value)} className={inputCls} data-testid="contact-hours" /></Field>
      </div>
      <Field label="Address"><textarea value={fd.address || ''} onChange={e => set('address', e.target.value)} rows={2} className={textareaCls} data-testid="contact-address" /></Field>
      <Field label="Google Maps Embed URL" hint="Paste embed src">
        <input type="text" value={fd.map_embed || ''} onChange={e => set('map_embed', e.target.value)} className={inputCls} placeholder="https://www.google.com/maps/embed?pb=..." data-testid="contact-map" />
        {fd.map_embed && <div className="mt-2 rounded-lg overflow-hidden border"><iframe src={fd.map_embed} width="100%" height="180" style={{border:0}} loading="lazy" title="Map" /></div>}
      </Field>
    </>
  );
}

/* ========== GALLERY EDITOR ========== */
function GalleryEditor({ images, onChange }) {
  const addImage = () => onChange([...images, { url: '', alt: '', caption: '' }]);
  const removeImage = (i) => { const n = [...images]; n.splice(i, 1); onChange(n); };
  const updateImage = (i, val) => { const n = [...images]; n[i] = { ...n[i], ...val }; onChange(n); };
  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-gray-700">Gallery Images</span>
        <button type="button" onClick={addImage} className="text-xs text-blue-600 hover:text-blue-800 font-medium" data-testid="gallery-add-image">+ Add Image</button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        {images.map((img, i) => (
          <div key={i} className="border border-gray-200 rounded-lg p-2 relative" data-testid={`gallery-item-${i}`}>
            <button type="button" onClick={() => removeImage(i)} className="absolute -top-1.5 -right-1.5 p-0.5 bg-red-500 text-white rounded-full z-10" data-testid={`gallery-remove-${i}`}><X size={12} /></button>
            <ImageUploader value={img.url || ''} onChange={url => updateImage(i, { url })} folder="cms" type="gallery" label="" className="mb-1" />
            <input type="text" value={img.caption || ''} onChange={e => updateImage(i, { caption: e.target.value })} className="w-full px-2 py-1 border border-gray-200 rounded text-xs" placeholder="Caption" data-testid={`gallery-caption-${i}`} />
          </div>
        ))}
      </div>
      {images.length === 0 && <p className="text-xs text-gray-400 italic py-4 text-center">No images yet.</p>}
    </div>
  );
}

export default AdminCMS;
